import { Box, FormControl, FormControlLabel, Grid, InputLabel, Select, Typography, Divider, TextField, Switch } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { Prompt, useHistory } from 'react-router';
import FormActionButtons from '../components/FormActionButtons';

import { CREATE, DataAPIContext } from '../api';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormLicenseeContext from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { formatTourOptionLabel } from '../components/tourOptionLabel';
import TimezonePicker from '../components/TimezonePicker';
import { ContextStore } from '../store/contextStore';
import { findMatchingOperatorDefault, tourUsesOperatorDefaults, tripHasDepartureCategory } from '../utils/tagCategories';
import TripDepartures from './TripDepartures';
import TripDefaultSettings from '../components/TripDefaultSettings2';
import { TRIP_TYPE_BUS, TRIP_TYPE_EXHIBITION, TRIP_TYPE_TOUR } from './utils';
import { v4 as uuidv4 } from 'uuid';

const useStyles = makeStyles((theme) => ({
    root: {},
    gridRoot: {
        margin: theme.spacing(2),
        minHeight: `calc(${theme.cp.contentHeight} - 7rem)`,
        border: `1px solid ${grey[200]}`,
        borderRadius: '14px',
        padding: '1rem',
        overflow: 'auto',
        [theme.breakpoints.down('sm')]: {
            maxHeight: `calc(${theme.cp.contentHeight} - 7rem)`,
            marginBottom: '4rem !important',

        },
    },
    tabButton: {
        borderRadius: '30px',
        textTransform: 'capitalize',
        fontWeight: 'normal',
    },
    buttonPanel: {
        marginTop: theme.spacing(2),
        backgroundColor: grey[100],
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        [theme.breakpoints.down('sm')]: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
        },
    },
}));

const validateInput = (trip, departures, isFranchise) => {
    const errors = {};
    let canSave = true;
    if (isFranchise && !trip.tour_operator) {
        errors.tour_operator = { error: true, helperText: 'Licensee is required' };
        canSave = false;
    }
    if (!trip.name || trip.name.length <= 0) {
        if (trip._changed_name) {
            errors.name = { error: true, helperText: 'Trip name is required' };
        }
        canSave = false;
    }
    if (!trip.group_tour) {
        if (trip._changed_tour) {
            errors.group_tour = { error: true, helperText: 'Template tour is required' };
        }
        canSave = false;
    }
    // CP-8136: a guide group's dates are inert -- its chat never expires and is
    // bucketed in its own section -- so neither the start date nor the time zone
    // is required for one. The backend defaults the start date to today.
    if (!trip.start_date && !trip.is_staff_chat_group) {
        if (trip._changed_date) {
            errors.start_date = { error: true, helperText: 'Start date is required' };
        }
        canSave = false;
    }
    if (!trip.start_timezone && !trip.is_staff_chat_group) {
        if (trip._changed_date) {
            errors.missing_timezone = { error: true, helperText: 'Time zone is required' };
        }
        canSave = false;
    }
    if (!trip.is_bespoke && departures && trip.departures.length < 1) {
        errors.departures = { error: true, helperText: 'At least one departure is required' };
        canSave = false;
    }

    return { canSave, errors };
};

const TripCreate = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'tour';
    const history = useHistory();
    const classes = useStyles();
    const saveRef = React.useRef(null);
    const [syncActive, setSyncActive] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm, isFranchise, currentLicensee, tourOperator } = React.useContext(ContextStore);
    const [leavePromptAcknowledged, setLeavePromptAcknowledged] = React.useState(false);

    const [isTouched, setIsTouched] = React.useState(false);

    const [clientErrors, setClientErrors] = React.useState({
        hasEmptyCheckinOrBoardingSelection: false,
        isMissingDeparture: false,
        isMissingBoarding: false,
    });

    const [trip, setTrip] = React.useState({
        id: 'new-trip', // Placeholder ID for create mode tag category handling
        name: '',
        atag: [],
        start_date: moment().utc(true).format('YYYY-MM-DD'),
        template_itinerary: null,
        allow_selfreg: false,
        is_bespoke: true,
        is_staff_chat_group: false,
        group_tour: null,
        start_timezone: null,
        trip_type: TRIP_TYPE_TOUR,
        tour_operator: currentLicensee || null,
        _changed: false,
        _changed_name: false,
        _changed_itinerary: false,
        _changed_date: false,
        departures: [],
        tag_categories: [],
    });
    // Counts the operator defaults the "Use Tour Operator default settings" switch brings in (CP-7922).
    const hasDepartureCategory = tripHasDepartureCategory(trip, tourOperator);
    const { canSave, errors } = validateInput(trip, hasDepartureCategory, isFranchise);

    const handleChange = (ev) => {
        setTrip((prevState) => ({ ...prevState, [ev.target.name]: ev.target.value, _changed: true, _changed_name: true }));
    };

    const onTourChange = (ev, nv) => {
        if (!nv) {
            setTrip((prevState) => ({ ...prevState, group_tour: null }));
            return;
        }

        // Create all new data before the state update
        const existing_tag_categories = [...nv.tag_categories];
        let lastUsedIndex = trip.tag_categories.length;
        const usesDefaults = tourUsesOperatorDefaults({ use_operator_defaults: nv.use_operator_defaults }, tourOperator);
        const operatorDefaults = usesDefaults ? (tourOperator?.default_tag_categories || []) : [];
        const new_associated_tag_categories = existing_tag_categories.map((tag, index) => ({
            ...tag,
            idx: uuidv4(),
            sequence: lastUsedIndex++,
            is_new: true,
            // Preserve inheritance: if template has group_tour=null, it's an inherited default category.
            // Template tour-level rows identical to an operator default also stay inherited — the
            // backend refuses to materialize them as tour-level copies anyway (CP-7983).
            group_tour: tag.group_tour != null && !findMatchingOperatorDefault(tag, operatorDefaults)
                ? 'new-trip'
                : null,
        }));

        // Single state update with all changes
        setTrip((prevState) => ({
            ...prevState,
            group_tour: nv,
            _changed: true,
            _changed_tour: true,
            // A guide group stays bespoke regardless of the template tour picked
            // (the backend forces this too).
            is_bespoke: prevState.is_staff_chat_group ? true : nv.is_bespoke,
            allow_selfreg: nv.allow_selfreg,
            use_operator_defaults: nv.use_operator_defaults,
            start_timezone: nv.start_timezone && nv.start_timezone !== "UTC" ? nv.start_timezone : prevState.start_timezone,
            atag: nv.atag,
            tag_categories: new_associated_tag_categories,
        }));
    };

    const onDateChange = (date) => {
        setTrip((prevState) => ({ ...prevState, start_date: date.format('YYYY-MM-DD'), _changed: true, _changed_date: true }));
    };

    const onLaunchPolicyChange = (ev, nv) => {
        console.log('launch policy: nv', nv);
        setTrip((prevState) => ({
            ...prevState,
            launch_policy: nv,
        }));
    };

    const handleSave = (ev) => {
        setIsTouched(true);

        let trip_placeholder = { ...trip };
        if (trip.start_timezone === 'UTC') {
            enqueueSnackbar('Please select a valid timezone', { variant: 'error' })
            return;
        }

        if (clientErrors.hasEmptyCheckinOrBoardingSelection) {
            enqueueSnackbar('Please select categories.', { variant: 'error' });
            return;
        }

        if (trip.tag_categories.filter((tc) => tc.category === null).length > 0) {
            enqueueSnackbar('All associated tag categories must have a category selected', { variant: 'error' });
            return;
        }

        // When the new tour uses operator defaults, inherited rows (group_tour: null) are
        // shown read-only and stay covered by the defaults — send only trip-level rows
        // (group_tour: 'new-trip'). When defaults are NOT in use, every row is shown as an
        // editable trip-level category, so all of them must be materialized on the new tour.
        const usesDefaults = tourUsesOperatorDefaults(trip_placeholder, tourOperator);
        trip_placeholder.tag_categories = trip_placeholder.tag_categories
            .filter((tac) => !usesDefaults || tac.group_tour === 'new-trip')
            .map((tac) => {
                const cleanedTac = { ...tac };
                delete cleanedTac.idx;
                // Remove placeholder group_tour used for create mode
                delete cleanedTac.group_tour;
                return cleanedTac;
            });
        // Remove placeholder id used for create mode
        delete trip_placeholder.id;

        //Check if trip is bespoke and has departures(must be removed before sending to backend)
        if (trip_placeholder.is_bespoke) {
            trip_placeholder.tag_categories = trip_placeholder.tag_categories.filter((tac) => tac.category.code !== 'departure');
            trip_placeholder.departures = [];
        }

        const params = { ...trip_placeholder };

        if (trip.template_itinerary && trip.trip_type === TRIP_TYPE_TOUR) {
            params.template_itinerary = trip.template_itinerary.id;
            delete params.group_tour;
        } else if (trip.group_tour) {
            params.group_tour = trip.group_tour.id;
            delete params.template_itinerary;
        }

        if (!params.is_bespoke) {
            params.schedule = 'RRULE:FREQ=WEEKLY;BYDAY=SU,MO,TU,WE,TH,FR,SA';
        }

        // let start_date_in_utc = getLocalDateFromUTC(trip.start_date, UTCTripStartTime, trip.start_timezone);

        // if (start_date_in_utc && trip.is_bespoke) {
        //     // adjust the start date so the selected date will be returned as the trip.datetime after creation
        //     params.start_date = start_date_in_utc;
        // }

        setSyncActive(true);
        (async () => {
            try {
                const res = await dataAPI(CREATE, resource, { data: params });
                if (res.statusCode === 201) {
                    setTrip({ ...trip, _changed: false });
                    history.push(`/trip/${res.data.id}`);
                } else if (res.statusCode === 406) {
                    enqueueSnackbar(res.data.errors, { variant: 'error' });
                } else if (res.statusCode === 409) {
                    // e.g. easy_go_live_required: the launch policy can't change before the account goes live.
                    // Nothing was created; the form keeps what the user entered.
                    enqueueSnackbar(res.data?.message || res.data?.detail || 'The trip could not be created', {
                        variant: 'error',
                    });
                }
            } finally {
                setSyncActive(false);
            }
        })();
    };

    const onSwitchChange = (ev) => {
        setTrip((prevState) => {
            let new_tag_categories = [...prevState.tag_categories];
            return { ...prevState, is_bespoke: !prevState.is_bespoke, _changed: true, tag_categories: new_tag_categories, _changed_date: true };
        });
    };

    const onUseOperatorDefaultsToggle = (ev) => {
        setTrip((prevState) => ({ ...prevState, use_operator_defaults: ev.target.checked, _changed: true }));
    };


    const onTimezoneChange = (ev, nv) => {
        setTrip((trip) => ({
            ...trip,
            start_timezone: nv?.value,
        }));
    };

    const onAccessTagsChange = (ev, atags, reason) => {
        setTrip((prevState) => ({ ...prevState, atag: atags }));
    };

    const onLicenseeChange = (id) => {
        setTrip((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    return (
        <div className={classes.root}>
            <FormLicenseeContext.Provider value={trip.tour_operator}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <PageMeta title="Create New User" />
                <Prompt when={!!trip._changed && leavePromptAcknowledged === false} message={() => { setLeavePromptAcknowledged(true); return "Are you sure you want to leave without saving changes?" }} />
                <FixedLinearProgress visibility={syncActive} />
                <div className={classes.gridRoot}>
                    <Grid container direction="row" spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h6">Create New Trip</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <LicenseeFormField value={trip.tour_operator} onChange={onLicenseeChange} error={errors.tour_operator?.error} helperText={errors.tour_operator?.helperText} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField {...errors.name} required variant="filled" size="small" fullWidth name="name" label="Name" value={trip.name} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl variant="filled" size="small" fullWidth>
                                <InputLabel>Trip Type</InputLabel>
                                <Select
                                    native
                                    value={trip.trip_type ?? TRIP_TYPE_TOUR}
                                    name="trip_type"
                                    onChange={(ev) => setTrip((prev) => ({ ...prev, trip_type: parseInt(ev.target.value), _changed: true }))}
                                >
                                    <option value={TRIP_TYPE_TOUR}>Tour</option>
                                    <option value={TRIP_TYPE_BUS}>Bus Trip</option>
                                    <option value={TRIP_TYPE_EXHIBITION}>Exhibition</option>
                                </Select>
                            </FormControl>
                        </Grid>
                        {/* CP-8136: dates and time zones mean nothing to a guide group -- its
                            chat never expires and is bucketed in its own section -- so showing
                            the controls would only invite a value that does nothing. The backend
                            fills the start date itself. */}
                        {!trip.is_staff_chat_group && (
                        <Grid item xs={12}>
                            <TimezonePicker label="Starting Timezone" required variant="filled" value={trip.start_timezone} onChange={onTimezoneChange} showTimezoneOffset={true} helperText="Time zone in which the trip starts" />
                        </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <AccessTagsDropdown value={trip.atag || []} onChange={onAccessTagsChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <RemoteAutocomplete
                                textFieldProps={{
                                    ...errors.launch_policy,
                                    placeholder: 'Default',
                                    helperText: 'If left empty, tour operator default will be used',
                                }}
                                InputProps={{ required: false }}
                                resource="launch_policy"
                                urlSuffix={''}
                                label="Launch Policy"
                                variant="filled"
                                size="small"
                                onChange={onLaunchPolicyChange}
                                value={trip.launch_policy}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <RemoteAutocomplete
                                required
                                textFieldProps={{ ...errors.group_tour }}
                                InputProps={{ required: true }}
                                resource="tour/templates"
                                urlSuffix={''}
                                label="Choose Template Tour"
                                variant="filled"
                                size="small"
                                onChange={onTourChange}
                                value={trip.group_tour}
                                getOptionLabel={(opt) => formatTourOptionLabel(opt)}
                                // CP-7940: the backend searches and pages the templates (an operator
                                // can have 1,500+): the list loads more as it scrolls, and opening it
                                // with a template picked searches nothing rather than its label.
                                pageSize={25}
                                filterOptions={(options) => options}
                                resetQueryOnOpen
                                deferFetchUntilOpen
                            />
                        </Grid>
                        {!trip.is_staff_chat_group && (
                        <Grid item xs={12} sm={6}>
                            <DatePicker
                                required
                                showTodayButton
                                disablePast
                                autoOk
                                todayLabel="today"
                                label="Start Date"
                                disabled={!trip.is_bespoke}
                                value={trip.start_date ? moment(trip.start_date) : null}
                                format="MMMM DD, YYYY"
                                onChange={onDateChange}
                                fullWidth
                                slotProps={{
                                    textField: {
                                        variant: 'filled',
                                        size: 'small',
                                    },
                                    actionBar: {
                                        actions: ['clear', 'accept'],
                                    },
                                }}
                            />
                        </Grid>
                        )}
                        {!trip.is_staff_chat_group && (
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                label="Non-Bespoke Tour"
                                control={<Switch name="is_bespoke" color="primary" checked={!trip.is_bespoke} onChange={onSwitchChange} />}
                            />
                        </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            {/* CP-8136: set once at creation, immutable afterwards -- the backend
                                edit path never touches the flag, so there is deliberately no way
                                to change it on the trip edit page. Guide groups are always
                                bespoke, and their dates are optional (chat neither expires them
                                nor buckets them by date). */}
                            <FormControlLabel
                                label="Guide group (staff-only chat; cannot be changed later)"
                                control={
                                    <Switch
                                        name="is_staff_chat_group"
                                        color="primary"
                                        checked={!!trip.is_staff_chat_group}
                                        onChange={(ev) =>
                                            setTrip((prevState) => ({
                                                ...prevState,
                                                is_staff_chat_group: ev.target.checked,
                                                is_bespoke: ev.target.checked ? true : prevState.is_bespoke,
                                                _changed: true,
                                            }))
                                        }
                                    />
                                }
                            />
                        </Grid>
                    </Grid>
                    {hasDepartureCategory && !trip.is_bespoke && (
                        <Grid container direction="row" spacing={2} mt={2}>
                            <Grid item xs={12}>
                                <TripDepartures 
                                    trip={trip}
                                    disabled={!hasPerm('tour', 'write')} 
                                    onChange={setTrip}
                                    clientErrors={clientErrors} 
                                    setClientErrors={setClientErrors}
                                />
                            </Grid>
                        </Grid>
                    )}
                    <Divider variant="middle" sx={{marginTop:'5px', marginBottom:'5px'}}/>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={!!trip.use_operator_defaults} onChange={onUseOperatorDefaultsToggle} name="use_operator_defaults" color="primary" />}
                                label="Use Tour Operator default settings"
                                disabled={!hasPerm('tour', 'write')}
                            />
                        </Grid>
                        <TripDefaultSettings
                            mode="create"
                            hasWritePerm={hasPerm('tour', 'write')}
                            hasUpdateTagCategoriesPerm={true}
                            hideGeneralSettings={true}
                            trip={trip}
                            onTripChange={setTrip}
                            clientErrors={clientErrors}
                            isParentTouched={isTouched}
                        />
                    </Grid>
                </div>
                <Box p={1} className={classes.buttonPanel}>
                    <FormActionButtons
                        submitRef={saveRef}
                        onSubmit={handleSave}
                        submitDisabled={!hasPerm('tour', 'write') || !canSave || syncActive}
                        onCancel={() => history.goBack()}
                    />
                </Box>
            </LocalizationProvider>
            </FormLicenseeContext.Provider>
        </div>
    );
};

export default TripCreate;
